import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import WebflowConnectCard from '../Settings/WebflowConnectCard';
import CrmImportPanel, { ExternalJob } from './CrmImportPanel';
import GsheetLeadSourcesList, { OpenJobRequest } from './GsheetLeadSourcesList';
import {
  Mail,
  Globe,
  CheckCircle,
  Circle,
  Lock,
  AlertCircle,
  RefreshCw,
  Plug,
  Clock,
  Users,
  Calendar,
  AlertTriangle,
  Shield,
  Info,
  FolderOpen,
} from 'lucide-react';
import {
  GlassCard,
  GlassButton,
  ModuleHeader,
} from '../ui';
import { useAuth } from '../../contexts/AuthContext';
import {
  integrationsApi,
  OAuthStatus,
  ImportJob,
  ScheduleStatus,
  ConsentStatus,
  ClioConnectionStatus,
} from '../../services/integrationsApi';
import { getCrmWritableProjects, Project } from '../../services/api';
import './Integrations.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null): string {
  if (!iso) return 'Never synced';
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function getTierInfo(user: any): { label: string; limit: number | null; canImport: boolean } {
  const tier = user?.tier || user?.plan || '';
  if (tier === 'enterprise') return { label: 'Unlimited', limit: null, canImport: true };
  if (tier === 'pro') return { label: 'Up to 5,000 contacts', limit: 5000, canImport: true };
  if (tier === 'growth') return { label: 'Up to 500 contacts', limit: 500, canImport: true };
  // Trial or unknown
  return { label: 'Upgrade required', limit: 0, canImport: false };
}

function getUserTier(user: any): string {
  return (user?.tier || user?.plan || 'trial').toLowerCase();
}

// DB/API uses 'ms' for Microsoft; frontend uses 'microsoft'
const PROVIDER_DB_KEY: Record<string, string> = { microsoft: 'ms', google: 'google' };
const toApiProvider = (p: string) => PROVIDER_DB_KEY[p] ?? p;

function getProviderStatus(statuses: OAuthStatus[], provider: string): OAuthStatus | null {
  const dbKey = PROVIDER_DB_KEY[provider] ?? provider;
  return statuses.find(s => s.provider === dbKey) ?? null;
}

/** Returns true if the provider token already has any calendar scope (read or write) */
function hasCalendarScopes(status: OAuthStatus): boolean {
  return status.scopes.some(
    s => s === 'Calendars.Read' || s === 'Calendars.ReadWrite' ||
         s === 'calendar.readonly' ||
         s === 'https://www.googleapis.com/auth/calendar.readonly' ||
         s === 'https://www.googleapis.com/auth/calendar.events' ||
         s === 'https://www.googleapis.com/auth/calendar' ||
         s.toLowerCase().includes('calendar'),
  );
}

/** Returns true if the provider token has calendar write scope (needed for booking sync) */
function hasCalendarWriteScopes(status: OAuthStatus): boolean {
  return status.scopes.some(
    s => s === 'Calendars.ReadWrite' ||
         s === 'https://www.googleapis.com/auth/calendar.events' ||
         s === 'https://www.googleapis.com/auth/calendar',
  );
}

/** Returns true if the provider token has email scopes */
function hasEmailScopes(status: OAuthStatus): boolean {
  return status.scopes.some(
    s => s === 'Mail.Read' || s === 'Mail.ReadBasic' ||
         s === 'https://www.googleapis.com/auth/gmail.readonly' ||
         s === 'https://mail.google.com/',
  );
}

/** Display label for job_type */
function formatJobType(jobType: string): string {
  if (jobType === 'contacts') return 'Contacts';
  if (jobType === 'calendar') return 'Calendar';
  if (jobType === 'email') return 'Emails';
  if (jobType === 'contacts_from_email') return 'Contacts (from Emails)';
  if (jobType === 'contacts_from_clio') return 'Contacts (from Clio)';
  return jobType;
}

// Date helpers
function toDateString(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDateString(d);
}
function daysAhead(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return toDateString(d);
}
function today(): string {
  return toDateString(new Date());
}

function resolveDateRange(
  window: string,
  customStart: string,
  customEnd: string,
): { start: string; end: string } {
  switch (window) {
    case 'last_30':  return { start: daysAgo(30), end: today() };
    case 'last_90':  return { start: daysAgo(90), end: today() };
    case 'last_180': return { start: daysAgo(180), end: today() };
    case 'last_365': return { start: daysAgo(365), end: today() };
    case 'next_30':  return { start: today(), end: daysAhead(30) };
    case 'custom':   return { start: customStart, end: customEnd };
    default:         return { start: daysAgo(30), end: today() };
  }
}

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

let _toastId = 0;

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

// ---------------------------------------------------------------------------
// StatusBadge
// ---------------------------------------------------------------------------

const StatusBadge: React.FC<{ status: ImportJob['status'] }> = ({ status }) => {
  const labels: Record<ImportJob['status'], string> = {
    pending: 'Pending',
    running: 'Running',
    done: 'Done',
    failed: 'Failed',
  };
  return (
    <span className={`status-badge status-badge--${status}`}>
      {status === 'running' && <span className="status-badge__dot status-badge__dot--animated" />}
      {labels[status]}
    </span>
  );
};

// ---------------------------------------------------------------------------
// ProviderCard
// ---------------------------------------------------------------------------

interface ProviderCardProps {
  provider: 'microsoft' | 'google';
  label: string;
  icon: React.ReactNode;
  status: OAuthStatus | null;
  onConnect: () => void;
  onDisconnect: () => void;
  connecting: boolean;
  disconnecting: boolean;
  onConnectCalendarWrite?: () => void;
  connectingCalendarWrite?: boolean;
}

const ProviderCard: React.FC<ProviderCardProps> = ({
  provider,
  label,
  icon,
  status,
  onConnect,
  onDisconnect,
  connecting,
  disconnecting,
  onConnectCalendarWrite,
  connectingCalendarWrite,
}) => {
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const connected = !!status;

  const handleDisconnectClick = () => {
    if (!confirmDisconnect) {
      setConfirmDisconnect(true);
      return;
    }
    onDisconnect();
    setConfirmDisconnect(false);
  };

  const handleCancelDisconnect = () => {
    setConfirmDisconnect(false);
  };

  return (
    <GlassCard className="provider-card">
      <div className="provider-card__header">
        <div className="provider-icon">{icon}</div>
        <div className="provider-card__info">
          <h3 className="provider-card__name">{label}</h3>
          <div className={`provider-status ${connected ? 'provider-status--connected' : 'provider-status--disconnected'}`}>
            <span className={`status-dot ${connected ? 'status-dot--connected' : 'status-dot--disconnected'}`} />
            <span>{connected ? 'Connected' : 'Not connected'}</span>
          </div>
        </div>
      </div>

      {connected && status && (
        <div className="provider-card__meta">
          <div className="provider-meta-row">
            <Clock size={13} />
            <span>Last contacts sync: {formatDate(status.last_contacts_sync)}</span>
          </div>
          <div className="provider-meta-row">
            <Clock size={13} />
            <span>Connected: {formatDate(status.connected_at)}</span>
          </div>
          <div className="provider-meta-row">
            <Calendar size={13} />
            {hasCalendarWriteScopes(status) ? (
              <span style={{ color: 'var(--success, #16a34a)', fontWeight: 'var(--font-semibold, 600)' }}>
                Calendar: Read + Write (booking sync ready)
              </span>
            ) : hasCalendarScopes(status) ? (
              <span style={{ color: 'var(--warning, #d97706)' }}>Calendar: Read only</span>
            ) : (
              <span style={{ color: 'var(--text-secondary)' }}>Calendar: Not connected</span>
            )}
          </div>
        </div>
      )}

      <div className="provider-card__actions">
        {connected ? (
          <>
            {confirmDisconnect ? (
              <div className="disconnect-confirm">
                <span className="disconnect-confirm__label">Disconnect {label}?</span>
                <GlassButton
                  variant="danger"
                  size="sm"
                  onClick={handleDisconnectClick}
                  loading={disconnecting}
                >
                  Confirm
                </GlassButton>
                <GlassButton
                  variant="secondary"
                  size="sm"
                  onClick={handleCancelDisconnect}
                >
                  Cancel
                </GlassButton>
              </div>
            ) : (
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={handleDisconnectClick}
                disabled={disconnecting}
              >
                Disconnect
              </GlassButton>
            )}
            {status && !hasCalendarWriteScopes(status) && onConnectCalendarWrite && (
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={onConnectCalendarWrite}
                loading={connectingCalendarWrite}
                disabled={connectingCalendarWrite}
              >
                <Calendar size={14} />
                Connect calendar (read + write)
              </GlassButton>
            )}
          </>
        ) : (
          <GlassButton
            variant="primary"
            size="sm"
            onClick={onConnect}
            loading={connecting}
          >
            <Plug size={14} />
            Connect {label}
          </GlassButton>
        )}
      </div>
    </GlassCard>
  );
};

// ---------------------------------------------------------------------------
// ImportSection — Contacts
// ---------------------------------------------------------------------------

interface ImportSectionProps {
  provider: 'microsoft' | 'google';
  providerLabel: string;
  tierInfo: { label: string; limit: number | null; canImport: boolean };
  onImport: (provider: string) => void;
  onImportFromEmails?: (provider: string) => void;
  syncing: string | null;
  activeJob: ImportJob | null;
  syncingFromEmails?: string | null;
  activeFromEmailsJob?: ImportJob | null;
}

const ImportSection: React.FC<ImportSectionProps> = ({
  provider,
  providerLabel,
  tierInfo,
  onImport,
  onImportFromEmails,
  syncing,
  activeJob,
  syncingFromEmails,
  activeFromEmailsJob,
}) => {
  const isThisProviderSyncing =
    syncing === provider ||
    (activeJob?.provider === provider && (activeJob.status === 'pending' || activeJob.status === 'running'));

  const isScrapingSyncing =
    syncingFromEmails === provider ||
    (activeFromEmailsJob?.provider === provider &&
      (activeFromEmailsJob.status === 'pending' || activeFromEmailsJob.status === 'running'));

  return (
    <GlassCard className="import-section">
      <h4 className="import-section__title">
        <Users size={16} />
        Import from {providerLabel}
      </h4>

      <ul className="scope-list">
        <li className="scope-item scope-item--enabled">
          <CheckCircle size={16} className="scope-item__icon scope-item__icon--check" />
          <span>Contacts</span>
        </li>
        <li className="scope-item scope-item--enabled">
          <CheckCircle size={16} className="scope-item__icon scope-item__icon--check" />
          <span>Calendar</span>
        </li>
        <li className="scope-item scope-item--enabled">
          <CheckCircle size={16} className="scope-item__icon scope-item__icon--check" />
          <span>Email Archiving</span>
        </li>
      </ul>

      <div className="tier-badge-row">
        <span className={`tier-badge ${!tierInfo.canImport ? 'tier-badge--upgrade' : ''}`}>
          {tierInfo.label}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <GlassButton
          variant="primary"
          onClick={() => onImport(provider)}
          disabled={!tierInfo.canImport || isThisProviderSyncing}
          loading={isThisProviderSyncing}
        >
          <RefreshCw size={14} />
          {isThisProviderSyncing ? 'Importing...' : 'Import Now'}
        </GlassButton>

        {onImportFromEmails && (
          <GlassButton
            variant="secondary"
            onClick={() => onImportFromEmails(provider)}
            disabled={!tierInfo.canImport || isScrapingSyncing}
            loading={isScrapingSyncing}
          >
            <Mail size={14} />
            {isScrapingSyncing ? 'Scraping...' : 'Scrape Contacts from Emails'}
          </GlassButton>
        )}
      </div>

      {activeFromEmailsJob && activeFromEmailsJob.provider === provider && activeFromEmailsJob.status === 'done' && (
        <div style={{ marginTop: 8, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          {activeFromEmailsJob.records_processed} contact(s) scraped from emails
        </div>
      )}
    </GlassCard>
  );
};

// ---------------------------------------------------------------------------
// CalendarSection — Phase 2
// ---------------------------------------------------------------------------

interface CalendarSectionProps {
  provider: 'microsoft' | 'google';
  providerLabel: string;
  status: OAuthStatus;
  tier: string;
  calendarWindow: string;
  setCalendarWindow: (v: string) => void;
  customStartDate: string;
  setCustomStartDate: (v: string) => void;
  customEndDate: string;
  setCustomEndDate: (v: string) => void;
  includeRecurring: boolean;
  setIncludeRecurring: (v: boolean) => void;
  onImportCalendar: (provider: string, startDate: string, endDate: string) => void;
  syncingCalendar: string | null;
  activeCalendarJob: ImportJob | null;
  onReconnect: (provider: 'microsoft' | 'google', scopeLevel: string) => void;
}

const CalendarSection: React.FC<CalendarSectionProps> = ({
  provider,
  providerLabel,
  status,
  tier,
  calendarWindow,
  setCalendarWindow,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  includeRecurring,
  setIncludeRecurring,
  onImportCalendar,
  syncingCalendar,
  activeCalendarJob,
  onReconnect,
}) => {
  const isProOrAbove = tier === 'pro' || tier === 'enterprise';
  const isEnterprise = tier === 'enterprise';
  const needsReconnect = !hasCalendarScopes(status);

  const isThisSyncing =
    syncingCalendar === provider ||
    (activeCalendarJob?.provider === provider &&
      (activeCalendarJob.status === 'pending' || activeCalendarJob.status === 'running'));

  const handleImportClick = () => {
    const { start, end } = resolveDateRange(calendarWindow, customStartDate, customEndDate);
    onImportCalendar(provider, start, end);
  };

  // Growth tier gate
  if (!isProOrAbove) {
    return (
      <div className="calendar-section">
        <div className="calendar-section__header">
          <Calendar size={16} />
          <span className="calendar-section__title">Calendar Import</span>
        </div>
        <div className="locked-panel">
          <Lock size={16} className="locked-panel-icon" />
          <div className="locked-panel__content">
            <span className="locked-panel__message">Calendar sync requires Pro or above</span>
            <span className="locked-panel__upgrade">Upgrade to unlock calendar integration</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-section">
      <div className="calendar-section__header">
        <Calendar size={16} />
        <span className="calendar-section__title">Calendar Import</span>
      </div>

      {needsReconnect && (
        <div className="reconnect-notice">
          <AlertTriangle size={14} className="reconnect-notice__icon" />
          <span>Calendar access requires reconnecting your account with additional permissions.</span>
          <GlassButton
            variant="secondary"
            size="sm"
            onClick={() => onReconnect(provider, 'calendar')}
          >
            Reconnect
          </GlassButton>
        </div>
      )}

      {!needsReconnect && (
        <>
          {/* Time window picker */}
          <div className="calendar-field-group">
            <label className="calendar-field-label">Time window</label>
            <select
              className="date-window-picker"
              value={calendarWindow}
              onChange={e => setCalendarWindow(e.target.value)}
            >
              <option value="last_30">Last 30 days</option>
              <option value="next_30">Next 30 days</option>
              <option value="last_90">Last 90 days</option>
              <option value="custom" disabled={!isEnterprise}>
                {isEnterprise ? 'Custom range' : 'Custom range (Enterprise only)'}
              </option>
            </select>
          </div>

          {/* Custom date range */}
          {calendarWindow === 'custom' && isEnterprise && (
            <div className="custom-date-range">
              <input
                type="date"
                className="date-input"
                value={customStartDate}
                onChange={e => setCustomStartDate(e.target.value)}
                aria-label="Start date"
              />
              <span className="custom-date-range__sep">to</span>
              <input
                type="date"
                className="date-input"
                value={customEndDate}
                onChange={e => setCustomEndDate(e.target.value)}
                aria-label="End date"
              />
            </div>
          )}

          {/* Recurring events toggle */}
          <div className={`calendar-recurring-row${!isEnterprise ? ' calendar-recurring-row--locked' : ''}`}>
            <label className={`calendar-recurring-label${!isEnterprise ? ' calendar-recurring-label--muted' : ''}`}>
              <input
                type="checkbox"
                checked={includeRecurring}
                onChange={e => isEnterprise && setIncludeRecurring(e.target.checked)}
                disabled={!isEnterprise}
                className="calendar-recurring-checkbox"
              />
              Include recurring event instances
            </label>
            {!isEnterprise && (
              <span className="calendar-recurring-hint">Available on Enterprise</span>
            )}
          </div>

          {/* Active calendar job result */}
          {activeCalendarJob && activeCalendarJob.provider === provider && activeCalendarJob.job_type === 'calendar' && activeCalendarJob.status === 'done' && (
            <div className="calendar-import-result">
              <CheckCircle size={15} className="calendar-import-result__icon" />
              <span>{activeCalendarJob.records_processed} meeting{activeCalendarJob.records_processed !== 1 ? 's' : ''} created in SuiteCRM</span>
            </div>
          )}

          <GlassButton
            variant="primary"
            onClick={handleImportClick}
            disabled={
              isThisSyncing ||
              (calendarWindow === 'custom' && (!customStartDate || !customEndDate))
            }
            loading={isThisSyncing}
          >
            <Calendar size={14} />
            {isThisSyncing ? 'Importing...' : 'Import Calendar'}
          </GlassButton>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// EmailSection — Phase 3
// ---------------------------------------------------------------------------

interface EmailSectionProps {
  provider: 'microsoft' | 'google';
  providerLabel: string;
  status: OAuthStatus;
  tier: string;
  hasConsent: boolean;
  emailWindow: string;
  setEmailWindow: (v: string) => void;
  emailCustomStart: string;
  setEmailCustomStart: (v: string) => void;
  emailCustomEnd: string;
  setEmailCustomEnd: (v: string) => void;
  emailFolderFilter: string;
  setEmailFolderFilter: (v: string) => void;
  emailPreviewCount: number | null;
  fetchingPreviewCount: string | null;
  onFetchPreviewCount: (provider: string) => void;
  onArchiveEmails: (provider: string) => void;
  syncingEmail: string | null;
  activeEmailJob: ImportJob | null;
  onReconnect: (provider: 'microsoft' | 'google', scopeLevel: string) => void;
  onOpenConsentModal: (provider: string) => void;
}

const EmailSection: React.FC<EmailSectionProps> = ({
  provider,
  providerLabel,
  status,
  tier,
  hasConsent,
  emailWindow,
  setEmailWindow,
  emailCustomStart,
  setEmailCustomStart,
  emailCustomEnd,
  setEmailCustomEnd,
  emailFolderFilter,
  setEmailFolderFilter,
  emailPreviewCount,
  fetchingPreviewCount,
  onFetchPreviewCount,
  onArchiveEmails,
  syncingEmail,
  activeEmailJob,
  onReconnect,
  onOpenConsentModal,
}) => {
  const isEnterprise = tier === 'enterprise';
  const isPro = tier === 'pro';
  const needsEmailReconnect = !hasEmailScopes(status);

  const isThisSyncing =
    syncingEmail === provider ||
    (activeEmailJob?.provider === provider &&
      (activeEmailJob.status === 'pending' || activeEmailJob.status === 'running'));

  const isFetchingCount = fetchingPreviewCount === provider;

  // Pro tier — locked panel
  if (isPro) {
    return (
      <div className="email-section">
        <div className="email-section__header">
          <Mail size={16} />
          <span className="email-section__title">Email Archiving</span>
        </div>
        <div className="locked-panel">
          <Lock size={16} className="locked-panel-icon" />
          <div className="locked-panel__content">
            <span className="locked-panel__message">Email archiving available on Enterprise</span>
            <span className="locked-panel__upgrade">Upgrade to Enterprise to archive emails into SuiteCRM</span>
          </div>
        </div>
      </div>
    );
  }

  // Growth/Trial — not shown (caller should gate this)
  if (!isEnterprise) {
    return null;
  }

  // Needs email scope reconnect
  if (needsEmailReconnect) {
    return (
      <div className="email-section">
        <div className="email-section__header">
          <Mail size={16} />
          <span className="email-section__title">Email Archiving</span>
        </div>
        <div className="reconnect-notice">
          <AlertTriangle size={14} className="reconnect-notice__icon" />
          <span>Email access requires reconnecting your account with additional permissions.</span>
          <GlassButton
            variant="secondary"
            size="sm"
            onClick={() => onReconnect(provider, 'email')}
          >
            Reconnect
          </GlassButton>
        </div>
      </div>
    );
  }

  // Consent not yet given
  if (!hasConsent) {
    return (
      <div className="email-section">
        <div className="email-section__header">
          <Mail size={16} />
          <span className="email-section__title">Email Archiving</span>
        </div>
        <div className="email-consent-prompt">
          <Shield size={18} className="email-consent-prompt__icon" />
          <div className="email-consent-prompt__body">
            <p className="email-consent-prompt__text">
              Archive emails directly into your SuiteCRM instance. Your consent is required before AiMe can access your email data.
            </p>
            <GlassButton
              variant="primary"
              size="sm"
              onClick={() => onOpenConsentModal(provider)}
            >
              <Shield size={14} />
              Enable Email Archiving
            </GlassButton>
          </div>
        </div>
      </div>
    );
  }

  // Folder filter helpers
  const selectedFolders = emailFolderFilter.split(',').filter(Boolean);
  const isAllFolders = selectedFolders.includes('all');

  const toggleFolder = (folder: string) => {
    if (folder === 'all') {
      setEmailFolderFilter('all');
      return;
    }
    // Deselect "all" if something specific is picked
    const without = selectedFolders.filter(f => f !== 'all' && f !== folder);
    if (selectedFolders.includes(folder)) {
      // Remove it (keep at least one)
      const next = without.length > 0 ? without : ['inbox'];
      setEmailFolderFilter(next.join(','));
    } else {
      setEmailFolderFilter([...without, folder].join(','));
    }
  };

  const { start: resolvedStart, end: resolvedEnd } = resolveDateRange(emailWindow, emailCustomStart, emailCustomEnd);

  const previewCountClass =
    emailPreviewCount === null
      ? ''
      : emailPreviewCount > 10000
      ? 'preview-count-result--over'
      : emailPreviewCount > 5000
      ? 'preview-count-result--warn'
      : 'preview-count-result--ok';

  return (
    <div className="email-section">
      <div className="email-section__header">
        <Mail size={16} />
        <span className="email-section__title">Email Archiving</span>
      </div>

      <div className="email-config">
        {/* Date range */}
        <div className="calendar-field-group">
          <label className="calendar-field-label">Date range</label>
          <select
            className="date-window-picker"
            value={emailWindow}
            onChange={e => setEmailWindow(e.target.value)}
          >
            <option value="last_30">Last 30 days</option>
            <option value="last_90">Last 90 days</option>
            <option value="last_180">Last 6 months</option>
            <option value="last_365">Last 12 months</option>
            <option value="custom">Custom range</option>
          </select>
        </div>

        {emailWindow === 'custom' && (
          <div className="custom-date-range">
            <input
              type="date"
              className="date-input"
              value={emailCustomStart}
              onChange={e => setEmailCustomStart(e.target.value)}
              aria-label="Start date"
            />
            <span className="custom-date-range__sep">to</span>
            <input
              type="date"
              className="date-input"
              value={emailCustomEnd}
              onChange={e => setEmailCustomEnd(e.target.value)}
              aria-label="End date"
            />
          </div>
        )}

        {/* Folder filter */}
        <div className="calendar-field-group">
          <label className="calendar-field-label">
            <FolderOpen size={13} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
            Folders
          </label>
          <div className="folder-filter-row">
            <label className="folder-checkbox-item">
              <input
                type="checkbox"
                className="calendar-recurring-checkbox"
                checked={!isAllFolders && selectedFolders.includes('inbox')}
                disabled={isAllFolders}
                onChange={() => toggleFolder('inbox')}
              />
              Inbox
            </label>
            <label className="folder-checkbox-item">
              <input
                type="checkbox"
                className="calendar-recurring-checkbox"
                checked={!isAllFolders && selectedFolders.includes('sent')}
                disabled={isAllFolders}
                onChange={() => toggleFolder('sent')}
              />
              Sent Items
            </label>
            <label className="folder-checkbox-item">
              <input
                type="checkbox"
                className="calendar-recurring-checkbox"
                checked={isAllFolders}
                onChange={() => toggleFolder('all')}
              />
              All Folders
            </label>
          </div>
        </div>

        {/* Preview count */}
        <div className="preview-count-row">
          <GlassButton
            variant="secondary"
            size="sm"
            onClick={() => onFetchPreviewCount(provider)}
            loading={isFetchingCount}
            disabled={
              isFetchingCount ||
              (emailWindow === 'custom' && (!emailCustomStart || !emailCustomEnd))
            }
          >
            <Info size={13} />
            Estimate volume
          </GlassButton>
          {emailPreviewCount !== null && !isFetchingCount && (
            <span className={`preview-count-result ${previewCountClass}`}>
              {emailPreviewCount > 10000
                ? `~${emailPreviewCount.toLocaleString()} emails — large import, may take a while`
                : `~${emailPreviewCount.toLocaleString()} emails in selected range`}
            </span>
          )}
        </div>

        {/* Post-import summary */}
        {activeEmailJob && activeEmailJob.provider === provider && activeEmailJob.job_type === 'email' && activeEmailJob.status === 'done' && (
          <div className="calendar-import-result">
            <CheckCircle size={15} className="calendar-import-result__icon" />
            <span>
              {activeEmailJob.records_processed} email{activeEmailJob.records_processed !== 1 ? 's' : ''} archived
            </span>
          </div>
        )}

        <GlassButton
          variant="primary"
          onClick={() => onArchiveEmails(provider)}
          disabled={
            !hasConsent ||
            isThisSyncing ||
            (emailWindow === 'custom' && (!emailCustomStart || !emailCustomEnd))
          }
          loading={isThisSyncing}
        >
          <Mail size={14} />
          {isThisSyncing ? 'Archiving...' : 'Archive Emails'}
        </GlassButton>

        <p className="compliance-footer">
          <Info size={12} className="compliance-footer-icon" />
          Email content is stored in your SuiteCRM instance only. AiMe does not retain email data after import.
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ConsentModal
// ---------------------------------------------------------------------------

interface ConsentModalProps {
  provider: string;
  providerLabel: string;
  onConfirm: (provider: string) => void;
  onClose: () => void;
  giving: boolean;
}

const ConsentModal: React.FC<ConsentModalProps> = ({
  provider,
  providerLabel,
  onConfirm,
  onClose,
  giving,
}) => {
  const [checked, setChecked] = useState(false);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="consent-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-modal-title"
    >
      <div
        className="consent-modal"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="consent-modal-title" id="consent-modal-title">
          <Shield size={18} />
          Enable Email Archiving
        </h3>

        <div className="consent-modal-body">
          <p>
            AiMe will access your {providerLabel} emails to archive them in your connected SuiteCRM instance. This helps track customer communications automatically.
          </p>
          <p>
            <strong>Important:</strong> AiMe does not store your email content. All data is transferred directly to SuiteCRM and is not retained on AiMe's servers after the import completes.
          </p>
          <p>
            You can revoke this access at any time by disconnecting your provider account.
          </p>
        </div>

        <label className="consent-checkbox-row">
          <input
            type="checkbox"
            className="calendar-recurring-checkbox"
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
          />
          I understand and consent to AiMe accessing my emails for archiving purposes
        </label>

        <div className="consent-modal-footer">
          <GlassButton variant="secondary" onClick={onClose} disabled={giving}>
            Cancel
          </GlassButton>
          <GlassButton
            variant="primary"
            onClick={() => onConfirm(provider)}
            disabled={!checked || giving}
            loading={giving}
          >
            <Shield size={14} />
            Enable Email Archiving
          </GlassButton>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ScheduleSection — Automatic Sync (Enterprise)
// ---------------------------------------------------------------------------

interface ScheduleSectionProps {
  provider: string;
  tier: string;
  schedules: ScheduleStatus[];
  onSaveSchedule: (provider: string, schedule: string | null) => Promise<void>;
  savingSchedule: string | null;
}

const ScheduleSection: React.FC<ScheduleSectionProps> = ({
  provider,
  tier,
  schedules,
  onSaveSchedule,
  savingSchedule,
}) => {
  const isEnterprise = tier === 'enterprise';
  const current = schedules.find(s => s.provider === provider);
  const [localSchedule, setLocalSchedule] = useState<string>(
    current?.schedule ?? 'off',
  );

  // Sync local value if schedules prop changes (e.g. after fetch)
  useEffect(() => {
    const found = schedules.find(s => s.provider === provider);
    setLocalSchedule(found?.schedule ?? 'off');
  }, [schedules, provider]);

  if (!isEnterprise) {
    return (
      <div className="schedule-section">
        <div className="locked-panel locked-panel--subtle">
          <Lock size={14} className="locked-panel-icon" />
          <span className="locked-panel__message">Automatic sync available on Enterprise</span>
          <span className="locked-panel__upgrade">Upgrade to unlock scheduled syncing</span>
        </div>
      </div>
    );
  }

  const isSaving = savingSchedule === provider;
  const nextRun = current?.next_run ? formatDate(current.next_run) : null;

  return (
    <div className="schedule-section">
      <div className="schedule-section__heading">Automatic Sync</div>
      <div className="schedule-row">
        <label className="schedule-row__label" htmlFor={`schedule-${provider}`}>
          Sync schedule
        </label>
        <select
          id={`schedule-${provider}`}
          className="date-window-picker schedule-row__select"
          value={localSchedule}
          onChange={e => setLocalSchedule(e.target.value)}
        >
          <option value="off">Off</option>
          <option value="hourly">Hourly</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
        <GlassButton
          variant="primary"
          size="sm"
          onClick={() => onSaveSchedule(provider, localSchedule === 'off' ? null : localSchedule)}
          loading={isSaving}
          disabled={isSaving}
        >
          Save
        </GlassButton>
      </div>
      {nextRun && localSchedule !== 'off' && (
        <div className="next-run-label">
          <Clock size={12} />
          Next sync: {nextRun}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// CrmTargetSection — crm-per-project-scoping: which project's CRM the
// Gmail/Outlook contact sync pushes into (default Unassigned, never guessed).
// ---------------------------------------------------------------------------

interface CrmTargetSectionProps {
  provider: string;          // 'microsoft' | 'google' (UI name)
  schedules: ScheduleStatus[];
  onSaved: () => Promise<void> | void;
  onToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const apiProviderOf = (provider: string): string => (provider === 'microsoft' ? 'ms' : provider);

const CrmTargetSection: React.FC<CrmTargetSectionProps> = ({ provider, schedules, onSaved, onToast }) => {
  const entry = schedules.find(s => s.provider === provider || s.provider === apiProviderOf(provider));
  const stored = entry?.crm_target_project_id ?? '';
  const [value, setValue] = useState<string>(stored);
  const [projectOptions, setProjectOptions] = useState<Project[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(stored); }, [stored]);
  useEffect(() => {
    getCrmWritableProjects().then(setProjectOptions).catch(() => setProjectOptions([]));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await integrationsApi.setCrmTarget(apiProviderOf(provider), value || null);
      await onSaved();
      onToast('Synced contacts project saved', 'success');
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      onToast(typeof d === 'string' ? d : 'Failed to save the project', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="schedule-section">
      <div className="schedule-row">
        <label className="schedule-row__label" htmlFor={`crm-target-${provider}`}>
          Add synced contacts to project
        </label>
        <select
          id={`crm-target-${provider}`}
          className="date-window-picker schedule-row__select"
          value={value}
          onChange={e => setValue(e.target.value)}
        >
          <option value="">Unassigned (decide later)</option>
          {projectOptions.map(p => (
            <option key={p.project_id} value={p.project_id}>{p.name}</option>
          ))}
        </select>
        <GlassButton
          variant="primary"
          size="sm"
          onClick={save}
          loading={saving}
          disabled={saving || value === stored}
        >
          Save
        </GlassButton>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ProgressSection
// ---------------------------------------------------------------------------

interface ProgressSectionProps {
  job: ImportJob;
}

const ProgressSection: React.FC<ProgressSectionProps> = ({ job }) => {
  if (job.status === 'done') {
    return (
      <GlassCard className="progress-section progress-section--done">
        <CheckCircle size={18} className="progress-section__icon progress-section__icon--success" />
        <span>
          {job.records_processed} contact{job.records_processed !== 1 ? 's' : ''} imported to SuiteCRM
        </span>
      </GlassCard>
    );
  }

  if (job.status === 'failed') {
    return (
      <GlassCard className="progress-section progress-section--failed">
        <AlertCircle size={18} className="progress-section__icon progress-section__icon--error" />
        <div>
          <span>Import failed</span>
          {job.error_log && job.error_log.length > 0 && (
            <p className="progress-section__error-detail">{job.error_log[0]}</p>
          )}
        </div>
      </GlassCard>
    );
  }

  // pending or running
  return (
    <GlassCard className="progress-section progress-section--running">
      <RefreshCw size={18} className="progress-section__icon progress-section__icon--spin" />
      <div>
        <span>Importing contacts...</span>
        {job.records_processed > 0 && (
          <p className="progress-section__count">{job.records_processed} processed so far</p>
        )}
      </div>
    </GlassCard>
  );
};

// ---------------------------------------------------------------------------
// HistoryTable
// ---------------------------------------------------------------------------

interface HistoryTableProps {
  jobs: ImportJob[];
}

const HistoryTable: React.FC<HistoryTableProps> = ({ jobs }) => {
  if (jobs.length === 0) {
    return (
      <div className="history-empty">
        <Clock size={32} className="history-empty__icon" />
        <p>Never imported</p>
      </div>
    );
  }

  return (
    <div className="history-table-wrapper">
      <table className="history-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Provider</th>
            <th>Type</th>
            <th>Records</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {jobs.slice(0, 10).map(job => (
            <tr key={job.id}>
              <td>{formatDate(job.created_at)}</td>
              <td className="history-table__provider">
                {(job.provider === 'ms' || job.provider === 'microsoft') ? <Mail size={13} /> :
                 job.provider === 'clio' ? <Users size={13} /> : <Globe size={13} />}
                {(job.provider === 'ms' || job.provider === 'microsoft') ? 'Microsoft 365' :
                 job.provider === 'clio' ? 'Clio' : 'Google Workspace'}
              </td>
              <td>{formatJobType(job.job_type)}</td>
              <td>
                {job.records_processed}
                {job.records_failed > 0 && (
                  <span className="history-table__failed"> ({job.records_failed} failed)</span>
                )}
              </td>
              <td>
                <StatusBadge status={job.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ClioSection
// ---------------------------------------------------------------------------

interface ClioSectionProps {
  status: ClioConnectionStatus | null;
  connecting: boolean;
  syncing: boolean;
  activeJob: ImportJob | null;
  onConnect: () => void;
  onSync: () => void;
}

const ClioSection: React.FC<ClioSectionProps> = ({
  status, connecting, syncing, activeJob, onConnect, onSync,
}) => {
  const connected = status?.connected ?? false;
  const isJobActive = !!(activeJob && (activeJob.status === 'pending' || activeJob.status === 'running'));

  return (
    <GlassCard className="provider-card">
      <div className="provider-card__header">
        <div className="provider-icon"><Users size={28} /></div>
        <div className="provider-card__info">
          <h3 className="provider-card__name">Clio</h3>
          <div className={`provider-status ${connected ? 'provider-status--connected' : 'provider-status--disconnected'}`}>
            <span className={`status-dot ${connected ? 'status-dot--connected' : 'status-dot--disconnected'}`} />
            <span>{connected ? 'Connected' : 'Not connected'}</span>
          </div>
        </div>
      </div>
      {connected && status?.connected_at && (
        <div className="provider-card__meta">
          <div className="provider-meta-row">
            <Clock size={13} />
            <span>Connected: {formatDate(status.connected_at)}</span>
          </div>
        </div>
      )}
      <div className="provider-card__actions">
        {!connected ? (
          <GlassButton variant="primary" size="sm" onClick={onConnect} loading={connecting}>
            <Plug size={14} />
            Connect Clio
          </GlassButton>
        ) : (
          <GlassButton
            variant="primary" size="sm" onClick={onSync}
            loading={syncing || isJobActive} disabled={syncing || isJobActive}
          >
            <Users size={14} />
            {syncing || isJobActive ? 'Importing...' : 'Scrape Contacts'}
          </GlassButton>
        )}
      </div>
      {activeJob && (
        <div style={{ marginTop: '12px' }}>
          <ProgressSection job={activeJob} />
        </div>
      )}
    </GlassCard>
  );
};

// ---------------------------------------------------------------------------
// Main module
// ---------------------------------------------------------------------------

interface OutletCtx {
  selectedProjectId?: string;
}

const IntegrationsModule: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const ctx = useOutletContext<OutletCtx>();
  const selectedProjectId = ctx?.selectedProjectId;

  const [statuses, setStatuses] = useState<OAuthStatus[]>([]);
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [activeJob, setActiveJob] = useState<ImportJob | null>(null);
  // Google Sheets syncing sources ↔ CRM import panel hand-off (Phase 3a).
  const [crmExternalJob, setCrmExternalJob] = useState<ExternalJob | null>(null);
  const [gsheetSourcesVersion, setGsheetSourcesVersion] = useState(0);
  const crmImportRef = useRef<HTMLElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Phase 2 — calendar state
  const [calendarWindow, setCalendarWindow] = useState<string>('last_30');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [includeRecurring, setIncludeRecurring] = useState<boolean>(false);
  const [syncingCalendar, setSyncingCalendar] = useState<string | null>(null);
  const [activeCalendarJob, setActiveCalendarJob] = useState<ImportJob | null>(null);
  const [schedules, setSchedules] = useState<ScheduleStatus[]>([]);
  const [savingSchedule, setSavingSchedule] = useState<string | null>(null);

  // Phase 3 — email archiving state
  const [consents, setConsents] = useState<ConsentStatus[]>([]);
  const [emailWindow, setEmailWindow] = useState<string>('last_30');
  const [emailCustomStart, setEmailCustomStart] = useState<string>('');
  const [emailCustomEnd, setEmailCustomEnd] = useState<string>('');
  const [emailFolderFilter, setEmailFolderFilter] = useState<string>('inbox,sent');
  const [emailPreviewCount, setEmailPreviewCount] = useState<number | null>(null);
  const [fetchingPreviewCount, setFetchingPreviewCount] = useState<string | null>(null);
  const [syncingEmail, setSyncingEmail] = useState<string | null>(null);
  const [activeEmailJob, setActiveEmailJob] = useState<ImportJob | null>(null);
  const [syncingFromEmails, setSyncingFromEmails] = useState<string | null>(null);
  const [activeFromEmailsJob, setActiveFromEmailsJob] = useState<ImportJob | null>(null);
  const [consentModalProvider, setConsentModalProvider] = useState<string | null>(null);
  const [givingConsent, setGivingConsent] = useState<boolean>(false);

  // Clio state
  const [clioStatus, setClioStatus] = useState<ClioConnectionStatus | null>(null);
  const [clioConnecting, setClioConnecting] = useState<boolean>(false);
  const [clioSyncing, setClioSyncing] = useState<boolean>(false);
  const [activeClioJob, setActiveClioJob] = useState<ImportJob | null>(null);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const calendarPollingRef = useRef<NodeJS.Timeout | null>(null);
  const emailPollingRef = useRef<NodeJS.Timeout | null>(null);
  const clioPollingRef = useRef<NodeJS.Timeout | null>(null);

  const tier = getUserTier(user);
  const showClioSection = user?.email === 'prath@capitalfive.com.au' || user?.role === 'superadmin';

  // ---- Toast helper ----
  const showToast = useCallback((message: string, type: Toast['type'] = 'success') => {
    const id = ++_toastId;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);

  // ---- Load data ----
  const loadStatus = useCallback(async () => {
    try {
      const res = await integrationsApi.getStatus();
      setStatuses(res.data);
    } catch (e) {
      console.error('Failed to load integration statuses:', e);
    }
  }, []);

  const loadJobs = useCallback(async () => {
    try {
      const res = await integrationsApi.getJobs();
      setJobs(res.data);
    } catch (e) {
      console.error('Failed to load import jobs:', e);
    }
  }, []);

  const loadSchedules = useCallback(async () => {
    try {
      const res = await integrationsApi.getSchedule();
      setSchedules(res.data);
    } catch (e) {
      console.error('Failed to load schedules:', e);
    }
  }, []);

  const loadConsents = useCallback(async () => {
    try {
      const res = await integrationsApi.getConsent();
      setConsents(res.data);
    } catch (e) {
      console.error('Failed to load consent statuses:', e);
    }
  }, []);

  const loadClioStatus = useCallback(async () => {
    try {
      const res = await integrationsApi.getClioStatus();
      setClioStatus(res.data);
    } catch (e) {
      console.error('Failed to load Clio status:', e);
    }
  }, []);

  // ---- Initial load ----
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const base = [loadStatus(), loadJobs(), loadSchedules(), loadConsents()];
      if (showClioSection) base.push(loadClioStatus());
      await Promise.all(base);
      setLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Handle OAuth redirect callback ----
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const connected = params.get('connected');
    if (connected === 'clio') {
      showToast('Successfully connected Clio', 'success');
      loadClioStatus();
      window.history.replaceState({}, '', location.pathname);
    } else if (connected === 'microsoft' || connected === 'google') {
      const label = connected === 'microsoft' ? 'Microsoft 365' : 'Google Workspace';
      showToast(`Successfully connected ${label}`, 'success');
      loadStatus();
      // Clean up URL without reload
      window.history.replaceState({}, '', location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Poll active contacts job ----
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback((jobId: number) => {
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const res = await integrationsApi.getJob(jobId);
        const job = res.data;
        setActiveJob(job);
        if (job.status === 'done' || job.status === 'failed') {
          stopPolling();
          setSyncing(null);
          if (job.status === 'done') {
            showToast(`${job.records_processed} contacts imported successfully`, 'success');
          } else {
            showToast('Import failed. See history for details.', 'error');
          }
          loadJobs();
        }
      } catch (e) {
        console.error('Polling error:', e);
        stopPolling();
        setSyncing(null);
      }
    }, 3000);
  }, [stopPolling, showToast, loadJobs]);

  // ---- Poll active calendar job ----
  const stopCalendarPolling = useCallback(() => {
    if (calendarPollingRef.current) {
      clearInterval(calendarPollingRef.current);
      calendarPollingRef.current = null;
    }
  }, []);

  const startCalendarPolling = useCallback((jobId: number) => {
    stopCalendarPolling();
    calendarPollingRef.current = setInterval(async () => {
      try {
        const res = await integrationsApi.getJob(jobId);
        const job = res.data;
        setActiveCalendarJob(job);
        if (job.status === 'done' || job.status === 'failed') {
          stopCalendarPolling();
          setSyncingCalendar(null);
          if (job.status === 'done') {
            showToast(`${job.records_processed} meeting${job.records_processed !== 1 ? 's' : ''} created in SuiteCRM`, 'success');
          } else {
            showToast('Calendar import failed. See history for details.', 'error');
          }
          loadJobs();
        }
      } catch (e) {
        console.error('Calendar polling error:', e);
        stopCalendarPolling();
        setSyncingCalendar(null);
      }
    }, 3000);
  }, [stopCalendarPolling, showToast, loadJobs]);

  // ---- Poll active email job ----
  const stopEmailPolling = useCallback(() => {
    if (emailPollingRef.current) {
      clearInterval(emailPollingRef.current);
      emailPollingRef.current = null;
    }
  }, []);

  const startEmailPolling = useCallback((jobId: number) => {
    stopEmailPolling();
    emailPollingRef.current = setInterval(async () => {
      try {
        const res = await integrationsApi.getJob(jobId);
        const job = res.data;
        setActiveEmailJob(job);
        if (job.status === 'done' || job.status === 'failed') {
          stopEmailPolling();
          setSyncingEmail(null);
          if (job.status === 'done') {
            showToast(`${job.records_processed} email${job.records_processed !== 1 ? 's' : ''} archived to SuiteCRM`, 'success');
          } else {
            showToast('Email archiving failed. See history for details.', 'error');
          }
          loadJobs();
        }
      } catch (e) {
        console.error('Email polling error:', e);
        stopEmailPolling();
        setSyncingEmail(null);
      }
    }, 3000);
  }, [stopEmailPolling, showToast, loadJobs]);

  // Clio polling
  const stopClioPolling = useCallback(() => {
    if (clioPollingRef.current) { clearInterval(clioPollingRef.current); clioPollingRef.current = null; }
  }, []);

  const startClioPolling = useCallback((jobId: number) => {
    stopClioPolling();
    clioPollingRef.current = setInterval(async () => {
      try {
        const res = await integrationsApi.getJob(jobId);
        const job = res.data;
        setActiveClioJob(job);
        if (job.status === 'done' || job.status === 'failed') {
          stopClioPolling();
          setClioSyncing(false);
          if (job.status === 'done') {
            showToast(`${job.records_processed} contact${job.records_processed !== 1 ? 's' : ''} imported from Clio`, 'success');
          } else {
            showToast('Clio import failed. See history for details.', 'error');
          }
          loadJobs();
        }
      } catch (e) { stopClioPolling(); setClioSyncing(false); }
    }, 3000);
  }, [stopClioPolling, showToast, loadJobs]);

  useEffect(() => {
    return () => {
      stopPolling();
      stopCalendarPolling();
      stopEmailPolling();
      stopClioPolling();
    };
  }, [stopPolling, stopCalendarPolling, stopEmailPolling, stopClioPolling]);

  // ---- Handlers ----
  const handleConnect = async (provider: 'microsoft' | 'google', scopeLevel: string = 'contacts') => {
    setConnecting(provider);
    try {
      const res = provider === 'microsoft'
        ? await integrationsApi.getMicrosoftAuthUrl(scopeLevel)
        : await integrationsApi.getGoogleAuthUrl(scopeLevel);
      window.location.href = res.data.auth_url;
    } catch (e: any) {
      showToast(getApiError(e, `Failed to initiate ${provider} connection`), 'error');
      setConnecting(null);
    }
  };

  const handleDisconnect = async (provider: string) => {
    setDisconnecting(provider);
    try {
      await integrationsApi.disconnect(toApiProvider(provider));
      showToast(`Disconnected ${provider === 'microsoft' ? 'Microsoft 365' : 'Google Workspace'}`, 'info');
      await loadStatus();
    } catch (e: any) {
      showToast(getApiError(e, 'Failed to disconnect'), 'error');
    } finally {
      setDisconnecting(null);
    }
  };

  const handleImport = async (provider: string) => {
    setSyncing(provider);
    try {
      const res = await integrationsApi.triggerSync(toApiProvider(provider), ['contacts']);
      const jobId = res.data.job_ids?.contacts;
      if (!jobId) { showToast('No contacts job was created', 'error'); setSyncing(null); return; }
      // Fetch initial job state
      const jobRes = await integrationsApi.getJob(jobId);
      setActiveJob(jobRes.data);
      startPolling(jobId);
    } catch (e: any) {
      showToast(getApiError(e, 'Failed to start import'), 'error');
      setSyncing(null);
    }
  };

  const handleImportCalendar = async (provider: string, startDate: string, endDate: string) => {
    setSyncingCalendar(provider);
    try {
      const res = await integrationsApi.triggerSync(
        toApiProvider(provider),
        ['calendar'],
        startDate,
        endDate,
      );
      const jobId = res.data.job_ids?.calendar;
      if (!jobId) { showToast('No calendar job was created', 'error'); setSyncingCalendar(null); return; }
      const jobRes = await integrationsApi.getJob(jobId);
      setActiveCalendarJob(jobRes.data);
      startCalendarPolling(jobId);
    } catch (e: any) {
      showToast(getApiError(e, 'Failed to start calendar import'), 'error');
      setSyncingCalendar(null);
    }
  };

  const handleArchiveEmails = async (provider: string) => {
    const { start, end } = resolveDateRange(emailWindow, emailCustomStart, emailCustomEnd);
    setSyncingEmail(provider);
    try {
      const res = await integrationsApi.triggerSync(
        toApiProvider(provider),
        ['email'],
        start,
        end,
        emailFolderFilter,
      );
      const jobId = res.data.job_ids?.email;
      if (!jobId) { showToast('No email job was created', 'error'); setSyncingEmail(null); return; }
      const jobRes = await integrationsApi.getJob(jobId);
      setActiveEmailJob(jobRes.data);
      startEmailPolling(jobId);
    } catch (e: any) {
      showToast(getApiError(e, 'Failed to start email archiving'), 'error');
      setSyncingEmail(null);
    }
  };

  const handleFetchPreviewCount = async (provider: string) => {
    const { start, end } = resolveDateRange(emailWindow, emailCustomStart, emailCustomEnd);
    setFetchingPreviewCount(provider);
    setEmailPreviewCount(null);
    try {
      const res = await integrationsApi.getPreviewCount(toApiProvider(provider), start, end, emailFolderFilter);
      setEmailPreviewCount(res.data.estimated_count);
    } catch (e: any) {
      showToast(getApiError(e, 'Failed to estimate email count'), 'error');
    } finally {
      setFetchingPreviewCount(null);
    }
  };

  const handleImportFromEmails = async (provider: string) => {
    if (provider === 'google') {
      showToast('Scraping contacts from email is available for Microsoft 365 only.', 'info');
      return;
    }
    // Check if token has mail scopes — if not, auto-reconnect at email level
    const status = statuses.find(s => s.provider === toApiProvider(provider));
    if (status && !hasEmailScopes(status)) {
      showToast(
        'Mail access is required to scrape contacts from emails. Reconnecting with email permissions…',
        'info',
      );
      handleReconnect(provider as 'microsoft' | 'google', 'email');
      return;
    }

    setSyncingFromEmails(provider);
    try {
      const res = await integrationsApi.triggerSync(toApiProvider(provider), ['contacts_from_email']);
      const jobId = res.data.job_ids?.contacts_from_email;
      if (!jobId) { showToast('No email scrape job was created', 'error'); setSyncingFromEmails(null); return; }
      const jobRes = await integrationsApi.getJob(jobId);
      setActiveFromEmailsJob(jobRes.data);

      // Poll every 3s until done or failed
      const poll = setInterval(async () => {
        try {
          const r = await integrationsApi.getJob(jobId);
          setActiveFromEmailsJob(r.data);
          if (r.data.status === 'done' || r.data.status === 'failed') {
            clearInterval(poll);
            setSyncingFromEmails(null);
            loadJobs();
            if (r.data.status === 'failed') {
              showToast('Email scrape failed. See history for details.', 'error');
            } else {
              showToast(`${r.data.records_processed} contact(s) scraped from emails`, 'success');
            }
          }
        } catch {
          clearInterval(poll);
          setSyncingFromEmails(null);
        }
      }, 3000);
    } catch (e: any) {
      showToast(getApiError(e, 'Failed to start email contact scrape'), 'error');
      setSyncingFromEmails(null);
    }
  };

  const handleOpenConsentModal = (provider: string) => {
    setConsentModalProvider(provider);
  };

  const handleCloseConsentModal = () => {
    setConsentModalProvider(null);
  };

  const handleGiveConsent = async (provider: string) => {
    setGivingConsent(true);
    try {
      await integrationsApi.giveEmailConsent(provider);
      // Update consents state locally
      setConsents(prev => {
        const existing = prev.find(c => c.provider === provider);
        if (existing) {
          return prev.map(c =>
            c.provider === provider
              ? { ...c, email_consent_at: new Date().toISOString() }
              : c,
          );
        }
        return [...prev, { provider, email_consent_at: new Date().toISOString() }];
      });
      showToast('Email archiving enabled', 'success');
      setConsentModalProvider(null);
    } catch (e: any) {
      showToast(getApiError(e, 'Failed to save consent'), 'error');
    } finally {
      setGivingConsent(false);
    }
  };

  const handleSaveSchedule = async (provider: string, schedule: string | null) => {
    setSavingSchedule(provider);
    try {
      await integrationsApi.setSchedule(provider, schedule);
      await loadSchedules();
      showToast('Sync schedule saved', 'success');
    } catch (e: any) {
      showToast(getApiError(e, 'Failed to save schedule'), 'error');
    } finally {
      setSavingSchedule(null);
    }
  };

  // Reconnect for scope upgrade (calendar or email)
  const handleReconnect = (provider: 'microsoft' | 'google', scopeLevel: string) => {
    handleConnect(provider, scopeLevel);
  };

  const handleClioConnect = async () => {
    setClioConnecting(true);
    try {
      const res = await integrationsApi.getClioAuthUrl();
      window.location.href = res.data.auth_url;
    } catch (e: any) {
      showToast(getApiError(e, 'Failed to initiate Clio connection'), 'error');
      setClioConnecting(false);
    }
  };

  const handleClioSync = async () => {
    setClioSyncing(true);
    try {
      const res = await integrationsApi.triggerClioSync();
      const jobId = res.data.job_id;
      const jobRes = await integrationsApi.getJob(jobId);
      setActiveClioJob(jobRes.data);
      startClioPolling(jobId);
    } catch (e: any) {
      showToast(getApiError(e, 'Failed to start Clio contact import'), 'error');
      setClioSyncing(false);
    }
  };

  // ---- Derived ----
  const hasEmailConsent = (provider: string): boolean =>
    consents.find(c => c.provider === provider)?.email_consent_at != null;

  const microsoftStatus = getProviderStatus(statuses, 'microsoft');
  const googleStatus = getProviderStatus(statuses, 'google');
  const tierInfo = getTierInfo(user);

  const connectedProviders = statuses.map(s => s.provider);
  const showActiveJob =
    activeJob &&
    (activeJob.status === 'pending' || activeJob.status === 'running' ||
      activeJob.status === 'done' || activeJob.status === 'failed');

  const showEmailSection = tier === 'enterprise';

  const consentModalProviderLabel =
    consentModalProvider === 'microsoft' ? 'Microsoft 365' :
    consentModalProvider === 'google' ? 'Google Workspace' : '';

  // ---- Render ----
  return (
    <div className="integrations-module">
      <ModuleHeader
        title="Integrations"
        breadcrumb={['Integrations']}
        actions={[]}
      />

      <div className="integrations-header">
        <p className="integrations-header__subtitle">
          Connect your email and calendar providers to import contacts and sync data.
        </p>
      </div>

      {loading ? (
        <div className="integrations-loading">
          <RefreshCw size={24} className="integrations-loading__icon" />
          <span>Loading integrations...</span>
        </div>
      ) : (
        <>
          {/* ---- Provider Cards ---- */}
          <section className="integrations-section">
            <h2 className="integrations-section__title">Connected Providers</h2>
            <div className="providers-grid">
              <ProviderCard
                provider="microsoft"
                label="Microsoft 365"
                icon={<Mail size={28} />}
                status={microsoftStatus}
                onConnect={() => handleConnect('microsoft')}
                onDisconnect={() => handleDisconnect('microsoft')}
                connecting={connecting === 'microsoft'}
                disconnecting={disconnecting === 'microsoft'}
                onConnectCalendarWrite={() => handleConnect('microsoft', 'calendar_write')}
                connectingCalendarWrite={connecting === 'microsoft'}
              />
              <ProviderCard
                provider="google"
                label="Google Workspace"
                icon={<Globe size={28} />}
                status={googleStatus}
                onConnect={() => handleConnect('google')}
                onDisconnect={() => handleDisconnect('google')}
                connecting={connecting === 'google'}
                disconnecting={disconnecting === 'google'}
                onConnectCalendarWrite={() => handleConnect('google', 'calendar_write')}
                connectingCalendarWrite={connecting === 'google'}
              />
              {showClioSection && (
                <ClioSection
                  status={clioStatus}
                  connecting={clioConnecting}
                  syncing={clioSyncing}
                  activeJob={activeClioJob}
                  onConnect={handleClioConnect}
                  onSync={handleClioSync}
                />
              )}
            </div>
          </section>

          {/* ---- Webflow CMS — productised; available on every tier with the integrations module ---- */}
          <section className="integrations-section">
            <h2 className="integrations-section__title">Content Publishing</h2>
            {selectedProjectId ? (
              <WebflowConnectCard
                projectId={selectedProjectId}
                pendingWebflowSiteId={
                  (new URLSearchParams(location.search).get('webflow_connected') === '1'
                    ? new URLSearchParams(location.search).get('webflow_site_id')
                    : null)
                }
              />
            ) : (
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', padding: '12px 0' }}>
                Select a project to manage Webflow CMS connections.
              </div>
            )}
          </section>

          {/* ---- Import leads into your CRM (file / Google Sheet import + syncing sheets) ---- */}
          <section className="integrations-section" ref={crmImportRef}>
            <h2 className="integrations-section__title">Import leads into your CRM</h2>
            <p className="integrations-section__subtitle">
              From another CRM, a spreadsheet, or a Google Sheet of Meta ad leads
            </p>
            <CrmImportPanel
              canImport={tierInfo.canImport}
              tierLabel={tierInfo.label}
              onToast={showToast}
              projectId={selectedProjectId}
              externalJob={crmExternalJob}
              onSourcesChanged={() => setGsheetSourcesVersion((v) => v + 1)}
            />
            <GsheetLeadSourcesList
              onToast={showToast}
              projectId={selectedProjectId}
              refreshKey={gsheetSourcesVersion}
              onOpenJob={(req: OpenJobRequest) => {
                setCrmExternalJob({ ...req, nonce: Date.now() });
                crmImportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            />
          </section>

          {/* ---- Import sections for connected providers ---- */}
          {connectedProviders.length > 0 && (
            <section className="integrations-section">
              <h2 className="integrations-section__title">Import Data</h2>
              <div className="import-sections-grid">
                {microsoftStatus && (
                  <GlassCard className="import-section-wrapper">
                    {/* Contacts import */}
                    <ImportSection
                      provider="microsoft"
                      providerLabel="Microsoft 365"
                      tierInfo={tierInfo}
                      onImport={handleImport}
                      onImportFromEmails={handleImportFromEmails}
                      syncing={syncing}
                      activeJob={activeJob}
                      syncingFromEmails={syncingFromEmails}
                      activeFromEmailsJob={activeFromEmailsJob}
                    />
                    {/* Calendar import */}
                    <CalendarSection
                      provider="microsoft"
                      providerLabel="Microsoft 365"
                      status={microsoftStatus}
                      tier={tier}
                      calendarWindow={calendarWindow}
                      setCalendarWindow={setCalendarWindow}
                      customStartDate={customStartDate}
                      setCustomStartDate={setCustomStartDate}
                      customEndDate={customEndDate}
                      setCustomEndDate={setCustomEndDate}
                      includeRecurring={includeRecurring}
                      setIncludeRecurring={setIncludeRecurring}
                      onImportCalendar={handleImportCalendar}
                      syncingCalendar={syncingCalendar}
                      activeCalendarJob={activeCalendarJob}
                      onReconnect={handleReconnect}
                    />
                    {/* Email archiving — Enterprise and Pro only */}
                    {showEmailSection && (
                      <EmailSection
                        provider="microsoft"
                        providerLabel="Microsoft 365"
                        status={microsoftStatus}
                        tier={tier}
                        hasConsent={hasEmailConsent('microsoft')}
                        emailWindow={emailWindow}
                        setEmailWindow={setEmailWindow}
                        emailCustomStart={emailCustomStart}
                        setEmailCustomStart={setEmailCustomStart}
                        emailCustomEnd={emailCustomEnd}
                        setEmailCustomEnd={setEmailCustomEnd}
                        emailFolderFilter={emailFolderFilter}
                        setEmailFolderFilter={setEmailFolderFilter}
                        emailPreviewCount={emailPreviewCount}
                        fetchingPreviewCount={fetchingPreviewCount}
                        onFetchPreviewCount={handleFetchPreviewCount}
                        onArchiveEmails={handleArchiveEmails}
                        syncingEmail={syncingEmail}
                        activeEmailJob={activeEmailJob}
                        onReconnect={handleReconnect}
                        onOpenConsentModal={handleOpenConsentModal}
                      />
                    )}
                    {/* Automatic sync schedule */}
                    <ScheduleSection
                      provider="microsoft"
                      tier={tier}
                      schedules={schedules}
                      onSaveSchedule={handleSaveSchedule}
                      savingSchedule={savingSchedule}
                    />
                    <CrmTargetSection
                      provider="microsoft"
                      schedules={schedules}
                      onSaved={loadSchedules}
                      onToast={showToast}
                    />
                  </GlassCard>
                )}
                {googleStatus && (
                  <GlassCard className="import-section-wrapper">
                    {/* Contacts import */}
                    <ImportSection
                      provider="google"
                      providerLabel="Google Workspace"
                      tierInfo={tierInfo}
                      onImport={handleImport}
                      syncing={syncing}
                      activeJob={activeJob}
                      syncingFromEmails={syncingFromEmails}
                      activeFromEmailsJob={activeFromEmailsJob}
                    />
                    {/* Calendar import */}
                    <CalendarSection
                      provider="google"
                      providerLabel="Google Workspace"
                      status={googleStatus}
                      tier={tier}
                      calendarWindow={calendarWindow}
                      setCalendarWindow={setCalendarWindow}
                      customStartDate={customStartDate}
                      setCustomStartDate={setCustomStartDate}
                      customEndDate={customEndDate}
                      setCustomEndDate={setCustomEndDate}
                      includeRecurring={includeRecurring}
                      setIncludeRecurring={setIncludeRecurring}
                      onImportCalendar={handleImportCalendar}
                      syncingCalendar={syncingCalendar}
                      activeCalendarJob={activeCalendarJob}
                      onReconnect={handleReconnect}
                    />
                    {/* No Google email archiving: every Gmail read scope is a Google restricted scope
                        (CASA-gated), so the Google connector never requests one. Microsoft only. */}
                    {/* Automatic sync schedule */}
                    <ScheduleSection
                      provider="google"
                      tier={tier}
                      schedules={schedules}
                      onSaveSchedule={handleSaveSchedule}
                      savingSchedule={savingSchedule}
                    />
                    <CrmTargetSection
                      provider="google"
                      schedules={schedules}
                      onSaved={loadSchedules}
                      onToast={showToast}
                    />
                  </GlassCard>
                )}
              </div>
            </section>
          )}

          {/* ---- Active job progress (contacts) ---- */}
          {showActiveJob && activeJob && (
            <section className="integrations-section">
              <h2 className="integrations-section__title">Import Progress</h2>
              <ProgressSection job={activeJob} />
            </section>
          )}

          {/* ---- Import History ---- */}
          <section className="integrations-section">
            <h2 className="integrations-section__title">Import History</h2>
            <HistoryTable jobs={jobs} />
          </section>
        </>
      )}

      {/* ---- Consent Modal ---- */}
      {consentModalProvider && (
        <ConsentModal
          provider={consentModalProvider}
          providerLabel={consentModalProviderLabel}
          onConfirm={handleGiveConsent}
          onClose={handleCloseConsentModal}
          giving={givingConsent}
        />
      )}

      {/* ---- Toasts ---- */}
      <div className="integrations-toasts">
        {toasts.map(t => (
          <div key={t.id} className={`integrations-toast integrations-toast--${t.type}`}>
            {t.type === 'success' && <CheckCircle size={15} />}
            {t.type === 'error' && <AlertCircle size={15} />}
            {t.type === 'info' && <Circle size={15} />}
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
};

export default IntegrationsModule;
